/**
 * Normalize raw time entry payloads (form / API) into the canonical shapes
 * used throughout the Hub UI.
 */

import type { CreateTimeEntryData, RawTimeEntryInput, StandardTimeEntry } from "./types";

function trimOrUndefined(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function trimOrNull(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * Clean time entry data for API submission
 */
export function cleanTimeEntryData(data: RawTimeEntryInput): CreateTimeEntryData {
  return {
    ticket_id: trimOrUndefined(data.ticket_id),
    description: data.description as string,
    hours: Number(data.hours),
    entry_type: (data.entry_type as CreateTimeEntryData["entry_type"]) || "billable",
    date_worked: data.date_worked as string,
    start_time: trimOrNull(data.start_time),
    end_time: trimOrNull(data.end_time),
    billable_rate: data.billable_rate ? Number(data.billable_rate) : null,
  };
}

/**
 * Standardize time entry data from API response
 */
export function standardizeTimeEntry(entry: RawTimeEntryInput): StandardTimeEntry {
  const ticketNumberRaw = entry.ticket?.ticket_number ?? entry.ticket_number;
  const ticketNumber =
    typeof ticketNumberRaw === "string" || typeof ticketNumberRaw === "number"
      ? String(ticketNumberRaw)
      : undefined;
  const formattedTicketNumber = ticketNumber ? `#${ticketNumber}` : "No Ticket";

  const firstName = typeof entry.user?.first_name === "string" ? entry.user.first_name : "";
  const lastName = typeof entry.user?.last_name === "string" ? entry.user.last_name : "";

  const fullName =
    (typeof entry.user?.full_name === "string" ? entry.user.full_name : undefined) ||
    (typeof entry.user_name === "string" ? entry.user_name : undefined) ||
    `${firstName} ${lastName}`.trim();

  return {
    id: entry.id as string,
    ticket_id: entry.ticket_id as string | undefined,
    ticket_number: ticketNumber,
    ticket_title:
      (typeof entry.ticket?.title === "string" ? entry.ticket.title : undefined) ||
      (typeof entry.ticket_title === "string" ? entry.ticket_title : undefined),
    client_name:
      (typeof entry.ticket?.client?.company_name === "string"
        ? entry.ticket.client.company_name
        : undefined) ||
      (typeof entry.ticket?.client?.name === "string" ? entry.ticket.client.name : undefined) ||
      (typeof entry.client_name === "string" ? entry.client_name : undefined),
    user_id: entry.user_id as string,
    user_name: fullName,
    user_email:
      (typeof entry.user?.email === "string" ? entry.user.email : undefined) ||
      (typeof entry.user_email === "string" ? entry.user_email : undefined),
    description: entry.description as string,
    hours: Number(entry.hours),
    entry_type: entry.entry_type as StandardTimeEntry["entry_type"],
    date_worked: entry.date_worked as string,
    start_time: entry.start_time as string | null | undefined,
    end_time: entry.end_time as string | null | undefined,
    billable_rate: entry.billable_rate ? Number(entry.billable_rate) : null,
    is_approved: Boolean(entry.is_approved),
    created_at: entry.created_at as string,
    updated_at: entry.updated_at as string,
    formatted_ticket_number: formattedTicketNumber,
  };
}
